import React, { useState, useEffect, useCallback } from "react";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
} from "../components/FioriUI";
import { config } from "../config";

// ─── Endpoints ────────────────────────────────────────────────────────────────
const ORDER_API_URL = "/api/order-processing/orders";
const WEBHOOK_URL = `/api/webhook/recipe/${config.orderProcessing.webhookRecipeId}`;
const POLL_URL = "/api/webhook/request";
const API_TOKEN = config.orderProcessing.apiToken;

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 30;

// ─── Webhook helper ───────────────────────────────────────────────────────────
async function triggerAndPoll(inputMessage) {
  const triggerRes = await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-api-token": API_TOKEN },
    body: JSON.stringify({ files: [], input_message: inputMessage, metadata: {} }),
  });

  if (!triggerRes.ok) throw new Error(`Webhook trigger failed: ${triggerRes.status}`);
  const { request_id } = await triggerRes.json();
  if (!request_id) throw new Error("No request_id in webhook response");

  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    const pollRes = await fetch(`${POLL_URL}/${request_id}`, {
      headers: { "x-api-token": API_TOKEN },
    });
    if (!pollRes.ok) continue;

    const data = await pollRes.json();
    const status = data?.status;

    if (status === "failed") throw new Error(data?.response || "Agent failed");

    if (status === "success") {
      const finalAnswer = data?.full_response?.response?.results?.[0]?.final_answer;
      const fallback = data?.response || "";

      const raw = finalAnswer || fallback;
      if (!raw) return { raw_response: "" };
      if (typeof raw === "object") return raw;

      const stripped = raw
        .replace(/^```json\s*/i, "")
        .replace(/^```\s*/, "")
        .replace(/\s*```$/, "")
        .trim();

      try { return JSON.parse(stripped); }
      catch {
        return { raw_response: stripped };
      }
    }
  }
  throw new Error("Agent timed out after 90 seconds");
}

const COLORS = {
  navy: "#131a33",
  blue: "#2447b0",
  blueDark: "#1d3fa8",
  blueSoft: "#e8eefb",
  blueText: "#1e3a8a",
  ink: "#111827",
  muted: "#6b7280",
  line: "#e5e7eb",
  page: "#f7f8fa",
  green: "#15803d",
  greenBg: "#eaf7ee",
  amber: "#a16207",
  amberBg: "#fdf4e3",
  red: "#b91c1c",
  redBg: "#fdecec",
};

const SOPS = {
  credit: {
    title: "O2C-OPS · Scenario Dispatch — Credit",
    sub: "Order Processing Exception Management Standard · v0.1 · draft for validation",
    rows: [
      { s: "CR_WITHIN_LIMIT", c: "Exposure ≤ 100% of limit, no adverse signal", d: "AUTO_RELEASE", t: "—" },
      { s: "CR_TOLERANCE_BAND", c: "Exposure 100–110%, clean aging, days-to-pay stable", d: "AUTO_RELEASE", t: "—" },
      { s: "CR_OVER_LIMIT_CLEAN", c: "Exposure 110–150%, no aging deterioration", d: "ESCALATE", t: "T2" },
      { s: "CR_AGING_DETERIORATION", c: "Any balance in 61–90 bucket, or days-to-pay worsened > 25% over 12 months", d: "ESCALATE", t: "T2", match: true },
      { s: "CR_SEVERE_EXPOSURE", c: "Exposure 150–200%", d: "ESCALATE", t: "T3" },
      { s: "CR_CRITICAL_EXPOSURE", c: "Exposure > 200%, or order > $100,000", d: "ESCALATE", t: "T4" },
      { s: "CR_NO_HISTORY", c: "No credit limit on file, or account under 90 days old", d: "CREDIT_APPLICATION", t: "—" },
      { s: "CR_BLOCKING", c: "Any 90+ balance, active collections, returned payment in 90 days", d: "HOLD", t: "T3 to override" },
    ],
    matchNote:
      "Matched on SO-44813. Days-to-pay moved 32 → 47 over 12 months, a 47% deterioration, and $9,410 sits in the 61–90 bucket. Either condition alone triggers this branch. Tolerance band resolved to 0% — the 61+ balance in the last six months voids the 3-year tenure allowance.",
    partialRule:
      "Where a scenario resolves to ESCALATE and no blocking signal is present, credit_check must compute a partial release that brings exposure back to 100% of limit, and name the specific invoice whose payment would clear the balance.",
    tolerance: [
      { tenure: "< 12 months", record: "Any", tol: "0%" },
      { tenure: "1–3 years", record: "Clean, stable", tol: "5%" },
      { tenure: "3+ years", record: "Clean, stable", tol: "10%" },
      { tenure: "3+ years", record: "Any 61+ balance in last 6 months", tol: "0%", match: true },
      { tenure: "Any", record: "Returned payment in last 90 days", tol: "0%" },
    ],
  },
  item: {
    title: "O2C-OPS · Scenario Dispatch — Item Master",
    sub: "Order Processing Exception Management Standard · v0.1 · draft for validation",
    rows: [
      { s: "IM_MATCHED", c: "Resolved, confidence ≥ 0.90, no substitution", d: "Accept line" },
      { s: "IM_SUPERSEDED", c: "Part discontinued, direct replacement exists", d: "Substitute, record substitution_from, state what changed", match: true },
      { s: "IM_MULTI_CANDIDATE", c: "Two or more candidates above 0.75", d: "NEEDS_REVIEW, list candidates" },
      { s: "IM_LOW_CONFIDENCE", c: "Extraction confidence below 0.60", d: "NEEDS_REVIEW, cite unreadable field" },
      { s: "IM_DISCONTINUED_NO_SUB", c: "Discontinued, no replacement on file", d: "NEEDS_REVIEW" },
      { s: "IM_PRICE_VARIANCE", c: "Quoted price differs from contract list by > 5%", d: "Flag, continue, do not override" },
      { s: "IM_NO_STOCK", c: "Ship-from branch has no stock", d: "Suggest alternate branch" },
    ],
    matchNote:
      "Two failure modes, kept distinct. IM_LOW_CONFIDENCE means the character could not be read — a human looks at the source document. IM_MULTI_CANDIDATE and IM_DISCONTINUED_NO_SUB mean the read was clean but does not resolve — a human makes a product decision. These route to different people.",
  },
};

function Pill({ tone, children }) {
  const map = {
    g: { bg: COLORS.greenBg, c: COLORS.green },
    a: { bg: COLORS.amberBg, c: COLORS.amber },
    r: { bg: COLORS.redBg, c: COLORS.red },
    b: { bg: COLORS.blueSoft, c: COLORS.blueText },
  };
  const s = map[tone];
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: 10.5,
        fontWeight: 600,
        padding: "4px 9px",
        borderRadius: 5,
        whiteSpace: "nowrap",
        background: s.bg,
        color: s.c,
      }}
    >
      {children}
    </span>
  );
}

function SopChip({ label, onClick }) {
  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: "#eef2ff",
        border: "1px solid #c7d2fe",
        color: "#3730a3",
        borderRadius: 5,
        padding: "3px 8px",
        fontSize: 10.5,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "ui-monospace,monospace",
      }}
    >
      {label}
    </span>
  );
}

function ActionLine({ tone, children }) {
  const map = { g: COLORS.green, a: COLORS.amber, r: COLORS.red };
  return <div style={{ color: map[tone], marginBottom: 3 }}>• {children}</div>;
}

function Kpi({ label, value, sub, tone }) {
  const toneColor = tone === "g" ? COLORS.green : tone === "a" ? COLORS.amber : tone === "r" ? COLORS.red : COLORS.ink;
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 27, fontWeight: 600, marginTop: 5, lineHeight: 1.1, color: toneColor }}>{value}</div>
      <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function Channel({ icon, name, count, status, on }) {
  return (
    <div
      style={{
        border: `1px solid ${on ? "#93b4f5" : COLORS.line}`,
        background: on ? "#f5f8ff" : "#fff",
        borderRadius: 9,
        padding: "12px 14px",
      }}
    >
      <div style={{ fontSize: 19 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 5, color: on ? COLORS.ink : "#9ca3af" }}>{name}</div>
      <div style={{ fontSize: 21, fontWeight: 600, marginTop: 3, color: on ? COLORS.blue : "#9ca3af" }}>{count}</div>
      <div style={{ fontSize: 10.5, color: on ? COLORS.blue : COLORS.muted }}>{status}</div>
    </div>
  );
}

function Row({ l, r, rColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f1f3f5", fontSize: 13 }}>
      <span style={{ color: COLORS.muted }}>{l}</span>
      <span style={{ textAlign: "right", fontWeight: 500, color: rColor }}>{r}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${COLORS.line}`, borderRadius: 9, overflow: "hidden", marginBottom: 14 }}>
      <h4
        style={{
          margin: 0,
          background: "#3560c9",
          color: "#fff",
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          padding: "9px 13px",
          fontWeight: 600,
        }}
      >
        {title}
      </h4>
      <div style={{ padding: 13 }}>{children}</div>
    </div>
  );
}

function Step({ n, status, desc, agent, tone }) {
  const dotBg = tone === "p" ? "#d97706" : tone === "s" ? "#9ca3af" : COLORS.blue;
  const boxBg = tone === "p" ? COLORS.amberBg : tone === "s" ? "#f3f4f6" : COLORS.blueSoft;
  const stColor = tone === "p" ? COLORS.amber : tone === "s" ? "#6b7280" : COLORS.blueText;
  return (
    <div style={{ display: "flex", gap: 11, marginBottom: 9 }}>
      <div
        style={{
          minWidth: 23,
          height: 23,
          borderRadius: "50%",
          background: dotBg,
          color: "#fff",
          fontSize: 11,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          marginTop: 1,
        }}
      >
        {n}
      </div>
      <div style={{ background: boxBg, borderRadius: 7, padding: "9px 11px", flex: 1 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, color: stColor }}>{status}</div>
        <div style={{ fontSize: 12.5, marginTop: 2, lineHeight: 1.5 }}>{desc}</div>
        <div style={{ fontSize: 10.5, color: COLORS.muted, marginTop: 3, fontFamily: "ui-monospace,monospace" }}>{agent}</div>
      </div>
    </div>
  );
}

export default function OrderProcessing() {
  // API data state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Sidebar data state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarError, setSidebarError] = useState(null);
  
  // UI state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sopKey, setSopKey] = useState(null);
  const [expandedActions, setExpandedActions] = useState({});
  const [filters, setFilters] = useState({
    decision: "",
    sopScenario: "",
    branch: "",
    search: "",
  });
  
  // Dynamic filter options
  const [branches, setBranches] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  
  // Stats/KPIs
  const [stats, setStats] = useState({
    total_orders: 0,
    auto_released: 0,
    escalated: 0,
    on_hold: 0,
    unique_branches: 0,
    auto_release_rate: 0,
    avg_time_to_release_minutes: 0
  });

  const anyOpen = drawerOpen || sopKey;
  const openSop = (k) => setSopKey(k);
  const closeSop = () => setSopKey(null);
  const closeAll = () => {
    setDrawerOpen(false);
    setSopKey(null);
  };

  const toggleActions = (orderId) =>
    setExpandedActions((prev) => ({ ...prev, [orderId]: !prev[orderId] }));

  const renderActionDetails = (actionDetails) => {
    if (!actionDetails || !Array.isArray(actionDetails) || actionDetails.length === 0) {
      return <span style={{ color: COLORS.muted }}>-</span>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {actionDetails.map((action, idx) => {
          const toneColor = 
            action.tone === "g" ? COLORS.green : 
            action.tone === "a" ? COLORS.amber : 
            action.tone === "r" ? COLORS.red : 
            COLORS.ink;
          
          return (
            <div key={idx} style={{ color: toneColor, fontSize: 14, lineHeight: 1.4, fontWeight: 500 }}>
              • {action.text || 'No description'}
            </div>
          );
        })}
      </div>
    );
  };

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters from filters
      const queryParams = new URLSearchParams();
      
      if (filters.decision) {
        queryParams.append('decision', filters.decision);
      }
      if (filters.sopScenario) {
        queryParams.append('sop_scenario', filters.sopScenario);
      }
      if (filters.branch) {
        queryParams.append('branch', filters.branch);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      
      const url = `${ORDER_API_URL}?${queryParams.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch orders");
      const data = await response.json();
      
      // Data is already transformed in backend, just use it directly
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message);
      setOrders([]); // Show empty state instead of sample data
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Fetch dynamic filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // Fetch branches
        const branchesRes = await fetch(`${ORDER_API_URL.replace('/orders', '')}/branches`);
        if (branchesRes.ok) {
          const branchesData = await branchesRes.json();
          setBranches(branchesData.branches || []);
        }
        
        // Fetch unique scenarios from orders
        const ordersRes = await fetch(ORDER_API_URL);
        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          const uniqueScenarios = [...new Set(
            (ordersData.orders || [])
              .map(o => o.sop)
              .filter(s => s && s !== 'N/A')
          )].sort();
          setScenarios(uniqueScenarios);
        }
        
        // Fetch stats
        const statsRes = await fetch(`${ORDER_API_URL.replace('/orders', '')}/stats`);
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    
    fetchFilterOptions();
  }, []);

  const handleRefresh = () => {
    fetchOrders();
  };
  
  const handleClearFilters = () => {
    setFilters({ decision: "", sopScenario: "", branch: "", search: "" });
  };

  // Fetch order details for sidebar
  const fetchOrderDetails = async (orderId) => {
    setSidebarLoading(true);
    setSidebarError(null);
    try {
      const response = await fetch(`${ORDER_API_URL}/${orderId}`);
      if (!response.ok) throw new Error("Failed to fetch order details");
      const data = await response.json();
      setSelectedOrder(data);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setSidebarError(err.message);
      setSelectedOrder(null);
    } finally {
      setSidebarLoading(false);
    }
  };

  const handleViewDetails = (orderId) => {
    setDrawerOpen(true);
    fetchOrderDetails(orderId);
  };

  // Handle order decision actions
  const handleOrderDecision = async (decision) => {
    if (!selectedOrder || !selectedOrder.order_id) {
      alert('No order selected');
      return;
    }

    const confirmMessages = {
      REJECTED: 'Are you sure you want to reject this order?',
      RELEASED: 'Are you sure you want to release this order in full?',
      PARTIAL_RELEASE: 'Are you sure you want to approve partial release for this order?'
    };

    if (!window.confirm(confirmMessages[decision])) {
      return;
    }

    try {
      const response = await fetch(`${ORDER_API_URL}/${selectedOrder.order_id}/decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: decision,
          approver: 'HUMAN_APPROVER', // Could be replaced with actual user email
          notes: `Order ${decision.toLowerCase()} via UI`
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || 'Failed to update order');
      }

      const result = await response.json();
      
      // Success - refresh the orders list and close sidebar
      alert(`Order ${decision.toLowerCase()} successfully!`);
      fetchOrders(); // Refresh the table
      setDrawerOpen(false);
      setSelectedOrder(null);
      
    } catch (err) {
      console.error('Error updating order decision:', err);
      alert(`Failed to update order: ${err.message}`);
    }
  };

  // Handle opening source document PDF
  const handleOpenSourceDocument = async (orderId) => {
    try {
      const response = await fetch(`${ORDER_API_URL}/${orderId}/presigned-url`);
      
      if (!response.ok) {
        throw new Error('Failed to get document URL');
      }
      
      const data = await response.json();
      
      if (data.presigned_url) {
        window.open(data.presigned_url, '_blank');
      } else {
        alert('No document URL available');
      }
    } catch (err) {
      console.error('Error opening source document:', err);
      alert(`Failed to open document: ${err.message}`);
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif", background: COLORS.page, color: COLORS.ink, fontSize: 14, minHeight: "100vh" }}>
      <div style={{ padding: "20px 22px 60px", maxWidth: 1500, margin: "0 auto" }}>
        {/* Head */}
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Order processing</h2>
            {/* <p style={{ margin: "2px 0 0", color: COLORS.muted, fontSize: 13 }}>Regency Supply · order intake, credit clearance, growth signal</p> */}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => window.open("/sop-editor?doc=order-processing", "_blank")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-fiori-border-dark rounded-fiori text-fiori-brand bg-transparent hover:bg-fiori-brand/5 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="w-7 h-7">
                <g transform="translate(1.407 1.407) scale(2.81 2.81)">
                  <path d="M24.731 57.127v-4.129c0-.444-.361-.806-.806-.806H20.05v5.74h3.876c.444 0 .805-.362.805-.805z"/>
                  <path d="M39.841 63.508V53.286c0-.604-.491-1.094-1.094-1.094H35.16v12.41h3.587c.604 0 1.094-.492 1.094-1.094z"/>
                  <path d="M81.979 17.233L66.215 1.469A5.013 5.013 0 0062.67 0H20.59c-2.764 0-5.013 2.249-5.013 5.013v36.168h-4.28c-2.617 0-4.745 2.084-4.745 4.646v25.139c0 2.563 2.128 4.646 4.745 4.646h4.28v9.374c0 2.764 2.249 5.013 5.013 5.013h57.844c2.764 0 5.013-2.249 5.013-5.013v-64.21a5.013 5.013 0 00-1.468-3.543zM18.05 68.603c-1.104 0-2-.896-2-2v-6.67v-9.74c0-1.104.896-2 2-2h5.876c2.65 0 4.806 2.156 4.806 4.806v4.129c0 2.649-2.156 4.806-4.806 4.806H20.05v4.67c0 1.103-.896 1.999-2 1.999zM79.447 84.987c0 .559-.454 1.013-1.013 1.013H20.59c-.559 0-1.013-.454-1.013-1.013v-9.374h44.425c2.616 0 4.745-2.084 4.745-4.646V45.828c0-2.562-2.129-4.646-4.745-4.646H19.578V5.013C19.578 4.454 20.032 4 20.59 4h41.328v12.784c0 2.617 2.129 4.745 4.745 4.745h12.784v63.458zM31.16 66.603v-16.41c0-1.104.896-2 2-2h5.587c2.809 0 5.094 2.285 5.094 5.094v10.222c0 2.81-2.285 5.095-5.094 5.095H33.16c-1.105 0-2-.896-2-2zM54.255 56.397c1.104 0 2 .896 2 2s-.896 2-2 2h-3.686v6.205c0 1.104-.896 2-2 2s-2-.896-2-2v-16.41c0-1.104.896-2 2-2h8.682c1.104 0 2 .896 2 2s-.896 2-2 2h-6.682v4.205h3.686z"/>
                </g>
              </svg>
              SOP
            </button>
            <button onClick={handleRefresh} style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff" }} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>


        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
          <Kpi 
            label="Orders this month" 
            value={stats.total_orders.toLocaleString()} 
            sub={`across ${stats.unique_branches} ${stats.unique_branches === 1 ? 'branch' : 'branches'}`} 
          />
          <Kpi 
            label="Auto-released" 
            value={stats.auto_released.toLocaleString()} 
            sub={`${stats.auto_release_rate}% straight through`} 
            tone="g" 
          />
          <Kpi 
            label="Escalated" 
            value={stats.escalated.toLocaleString()} 
            sub="credit review queue" 
            tone="a" 
          />
          <Kpi 
            label="On hold" 
            value={stats.on_hold.toLocaleString()} 
            sub="action required" 
            tone="r" 
          />
          <Kpi 
            label="Avg time to release" 
            value={stats.avg_time_to_release_minutes > 0 ? `${stats.avg_time_to_release_minutes} min` : 'N/A'} 
            sub={stats.avg_time_to_release_minutes > 60 ? `${(stats.avg_time_to_release_minutes / 60).toFixed(1)} hours` : 'real-time processing'} 
          />
        </div>
       

        {/* Filters */}
        <FilterBar title="Filter Orders" onClear={handleClearFilters}>
          <Field label="Credit decision" htmlFor="decision">
            <Select
              id="decision"
              value={filters.decision}
              onChange={(e) => setFilters((p) => ({ ...p, decision: e.target.value }))}
            >
              <option value="">All decisions</option>
              <option value="AUTO_RELEASE">Auto-released</option>
              <option value="ESCALATE">Escalated</option>
              <option value="CREDIT_APPLICATION">Credit application</option>
              <option value="HOLD">On hold</option>
            </Select>
          </Field>
          <Field label="SOP scenario matched" htmlFor="sopScenario">
            <Select
              id="sopScenario"
              value={filters.sopScenario}
              onChange={(e) => setFilters((p) => ({ ...p, sopScenario: e.target.value }))}
            >
              <option value="">All scenarios</option>
              {scenarios.map(scenario => {
                // Format scenario code to be more readable
                // CR_WITHIN_LIMIT -> Within Limit
                // IM_SUPERSEDED -> Superseded
                const formatScenario = (code) => {
                  if (!code) return code;
                  // Remove prefix (CR_, IM_, CM_) and convert to title case
                  return code
                    .replace(/^(CR_|IM_|CM_)/, '')
                    .split('_')
                    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
                    .join(' ');
                };
                
                return (
                  <option key={scenario} value={scenario}>
                    {formatScenario(scenario)}
                  </option>
                );
              })}
            </Select>
          </Field>
          <Field label="Branch" htmlFor="branch">
            <Select
              id="branch"
              value={filters.branch}
              onChange={(e) => setFilters((p) => ({ ...p, branch: e.target.value }))}
            >
              <option value="">All branches</option>
              {branches.map(branch => (
                <option key={branch.branch} value={branch.branch_name || branch.branch}>
                  {branch.branch_name || branch.branch}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Search" htmlFor="search">
            <SearchInput
              id="search"
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              placeholder="Order, customer, or PO number"
            />
          </Field>
        </FilterBar>

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", border: `1px solid ${COLORS.line}` }}>
          <thead>
            <tr>
              {["Order", "Customer", "Value", "Decision", "AI actions", "Growth signal", "Details"].map((h) => (
                <th
                  key={h}
                  style={{
                    background: COLORS.blue,
                    color: "#fff",
                    fontSize: 14,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    textAlign: "left",
                    padding: "12px 12px",
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Spinner /> Loading orders...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>
                  Error: {error}
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}>
                  <EmptyState 
                    title="No orders found" 
                    description="No orders match the current filters or there are no orders in the system."
                  />
                </td>
              </tr>
            ) : (
              orders.map((o, i) => (
              <tr key={o.id} style={{ background: o.flag ? "#fffdf6" : "transparent" }}>
                <td style={tdStyle(i, orders.length)}>
                  <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 16, fontWeight: 600 }}>{o.id}</div>
                  <div style={{ color: COLORS.muted, fontSize: 16, marginTop: 3 }}>{o.po}</div>
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.customer}
                  <div style={{ color: COLORS.muted, fontSize: 16, marginTop: 3 }}>
                    {o.branch} · <SopChip label={o.sop} onClick={() => openSop(o.sopKey)} />
                  </div>
                </td>
                <td style={tdStyle(i, orders.length)}>{o.value}</td>
                <td style={tdStyle(i, orders.length)}>
                  <Pill tone={o.pill}>{o.decision}</Pill>
                </td>
                <td style={tdStyle(i, orders.length)}>
                  {o.actions && o.actions.length > 0 ? (
                    <div style={{ background: "#f9fafb", border: `1px solid ${COLORS.line}`, borderRadius: 7, padding: "9px 11px", fontSize: 14, lineHeight: 1.65, maxWidth: 400 }}>
                      <div style={!expandedActions[o.id] ? { overflow: "hidden", maxHeight: "42px" } : {}}>
                        {renderActionDetails(o.actions)}
                      </div>
                      {o.actions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => toggleActions(o.id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: COLORS.blue,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                            padding: 0,
                            marginTop: 4,
                            textDecoration: "none",
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                          onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                        >
                          {expandedActions[o.id] ? "Show less" : "Show more"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: COLORS.muted }}>-</span>
                  )}
                </td>
                <td style={tdStyle(i, orders.length)}>
                  <Pill tone={o.growthTone || "b"}>{o.growth}</Pill>
                  <div style={{ color: COLORS.muted, fontSize: 16, marginTop: 3 }}>{o.growthSub}</div>
                </td>
                <td style={tdStyle(i, orders.length)}>
                  <button onClick={() => handleViewDetails(o.id)} style={eyeStyle}>
                    👁
                  </button>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </table>
      </div>

      {/* Scrim */}
      {anyOpen && (
        <div
          onClick={closeAll}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 40 }}
        />
      )}
      {/* Order drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(620px, 94vw)",
          background: COLORS.page,
          zIndex: 50,
          transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform .22s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ background: COLORS.blueDark, color: "#fff", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Credit decision review</h3>
          <button onClick={closeAll} style={dcloseStyle}>
            ×
          </button>
        </div>
        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {sidebarLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spinner /> Loading order details...
            </div>
          ) : sidebarError ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.red }}>
              Error: {sidebarError}
            </div>
          ) : !selectedOrder ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: COLORS.muted }}>
              No order selected
            </div>
          ) : (
            <>
              <Card title="Order details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <KV k="Order" v={selectedOrder.order_id || selectedOrder.run_id} mono />
                  <KV k="Customer PO" v={selectedOrder.customer_po || 'N/A'} mono />
                  <KV k="Customer" v={selectedOrder.customer_name || 'Unknown'} />
                  <KV k="Branch" v={selectedOrder.branch_name || selectedOrder.branch || 'N/A'} />
                  <KV k="Order value" v={selectedOrder.order_total ? `$${parseFloat(selectedOrder.order_total).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '$0.00'} />
                  <div>
                    <div style={kStyle}>SOP scenario</div>
                    <div style={{ marginTop: 2 }}>
                      <SopChip 
                        label={selectedOrder.scenario_code || 'N/A'} 
                        onClick={() => openSop(selectedOrder.scenario_code?.toLowerCase().includes('cr_') ? 'credit' : 'item')} 
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 11, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <Pill tone={
                    selectedOrder.credit_decision === 'AUTO_RELEASE' ? 'g' : 
                    selectedOrder.credit_decision === 'ESCALATE' ? 'a' : 
                    selectedOrder.credit_decision === 'HOLD' ? 'r' : 'b'
                  }>
                    {selectedOrder.credit_decision === 'AUTO_RELEASE' ? 'Auto-released' :
                     selectedOrder.credit_decision === 'ESCALATE' ? 'Escalated' :
                     selectedOrder.credit_decision === 'HOLD' ? 'On hold' :
                     selectedOrder.credit_decision === 'CREDIT_APPLICATION' ? 'Credit application' :
                     selectedOrder.credit_decision || 'Pending'}
                  </Pill>
                  {selectedOrder.credit_evidence?.approval_tier && (
                    <span style={{ background: "#ede9fe", color: "#5b21b6", borderRadius: 5, padding: "3px 8px", fontSize: 10.5, fontWeight: 700 }}>
                      {selectedOrder.credit_evidence.approval_tier} — {
                        selectedOrder.credit_evidence.approval_tier === 'T1' ? 'Supervisor' :
                        selectedOrder.credit_evidence.approval_tier === 'T2' ? 'Credit manager' :
                        selectedOrder.credit_evidence.approval_tier === 'T3' ? 'Credit director' :
                        selectedOrder.credit_evidence.approval_tier === 'T4' ? 'CFO' : 'Approver'
                      }
                    </span>
                  )}
                  {selectedOrder.credit_evidence?.sla_hours > 0 && selectedOrder.credit_evidence?.sla_due_at && (
                    <span style={{ fontSize: 11, color: COLORS.amber, fontWeight: 600 }}>
                      SLA {selectedOrder.credit_evidence.sla_hours}h · {(() => {
                        const due = new Date(selectedOrder.credit_evidence.sla_due_at);
                        const now = new Date();
                        const diffMs = due - now;
                        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        return diffHrs >= 0 ? `${diffHrs}h ${diffMins}m remaining` : 'Overdue';
                      })()}
                    </span>
                  )}
                </div>
              </Card>

              <Card title="Source document and extraction">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  {/* Left side - Source document */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 10 }}>
                      Source document
                    </div>
                    
                    {/* Customer info box */}
                    <div style={{ background: "#f9fafb", border: `1px solid ${COLORS.line}`, borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.ink }}>
                        {selectedOrder.customer_name?.toUpperCase() || 'CUSTOMER NAME'}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                        Customer ID: {selectedOrder.customer_id || 'N/A'}
                      </div>
                      {selectedOrder.source_document?.filename && (
                        <div style={{ marginTop: 8 }}>
                          <button
                            onClick={() => handleOpenSourceDocument(selectedOrder.order_id || selectedOrder.run_id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: COLORS.blue,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              padding: 0,
                              textDecoration: 'underline',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
                              <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm2.25 8.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" clipRule="evenodd" />
                            </svg>
                            View Source Document (PDF)
                          </button>
                        </div>
                      )}
                    </div>

                    {/* PO info */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600 }}>PURCHASE ORDER</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.blue, marginTop: 2, fontFamily: "ui-monospace,monospace" }}>
                        {selectedOrder.customer_po || 'N/A'}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
                        Date {selectedOrder.order_date || 'N/A'}
                      </div>
                    </div>

                    {/* Order lines from source */}
                    <div style={{ marginTop: 16 }}>
                      {selectedOrder.lines && selectedOrder.lines.length > 0 ? (
                        selectedOrder.lines.map((line, idx) => (
                          <div key={idx} style={{ marginBottom: 12, fontSize: 12 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                              <span style={{ color: COLORS.blue, fontWeight: 600, fontSize: 14 }}>
                                {line.qty || 0}
                              </span>
                              <span style={{ color: COLORS.muted }}>ea</span>
                              <span style={{ color: COLORS.ink }}>
                                {line.description || line.raw_reference || 'Unknown item'}
                              </span>
                            </div>
                            <div style={{ marginLeft: 40, marginTop: 2 }}>
                              {line.item_id && (
                                <span style={{ 
                                  fontFamily: "ui-monospace,monospace", 
                                  fontSize: 11,
                                  background: line.status === 'NEEDS_REVIEW' ? '#fef3c7' : '#f3f4f6',
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  color: line.status === 'NEEDS_REVIEW' ? '#92400e' : COLORS.muted
                                }}>
                                  {line.item_id}
                                </span>
                              )}
                              {line.superseded_from && (
                                <span style={{ fontSize: 11, color: '#92400e', marginLeft: 8 }}>
                                  (was: {line.superseded_from})
                                </span>
                              )}
                              {line.status === 'NEEDS_REVIEW' && (
                                <span style={{ fontSize: 11, color: '#dc2626', marginLeft: 8 }}>
                                  (needs review)
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>No line items</div>
                      )}
                    </div>

                    {/* Delivery date */}
                    {selectedOrder.requested_date && (
                      <div style={{ marginTop: 16, fontSize: 12 }}>
                        <span style={{ color: COLORS.muted }}>Deliver by </span>
                        <span style={{ color: COLORS.blue, fontWeight: 600 }}>{selectedOrder.requested_date}</span>
                      </div>
                    )}
                  </div>

                  {/* Right side - Extracted order */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, marginBottom: 10 }}>
                      Extracted order
                    </div>

                    {/* Account */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: COLORS.muted }}>Account</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.ink, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                        {selectedOrder.customer_id || 'N/A'}
                        <span style={{ color: COLORS.green, fontSize: 18 }}>✓</span>
                      </div>
                    </div>

                    {/* Terms */}
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: COLORS.muted }}>Terms</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.ink, marginTop: 2 }}>
                        {/* Extract payment terms from credit_evidence or lines if available */}
                        Net 30
                      </div>
                    </div>

                    {/* Requested date */}
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: COLORS.muted }}>Requested date</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.ink, marginTop: 2 }}>
                        {selectedOrder.requested_date || selectedOrder.order_date || 'N/A'}
                      </div>
                    </div>

                    {/* Extracted lines with resolution status */}
                    <div>
                      {selectedOrder.lines && selectedOrder.lines.length > 0 ? (
                        selectedOrder.lines.map((line, idx) => {
                          const bgColor = 
                            line.scenario === 'IM_MATCHED' ? '#eaf7ee' :
                            line.scenario === 'IM_SUPERSEDED' ? '#fef3c7' :
                            line.scenario === 'IM_MULTI_CANDIDATE' || line.scenario === 'IM_LOW_CONFIDENCE' || line.scenario === 'IM_DISCONTINUED_NO_SUB' ? '#fdecec' :
                            '#f3f4f6';
                          
                          const textColor =
                            line.scenario === 'IM_MATCHED' ? COLORS.green :
                            line.scenario === 'IM_SUPERSEDED' ? '#92400e' :
                            '#dc2626';

                          return (
                            <div key={idx} style={{ 
                              background: bgColor,
                              border: `1px solid ${line.scenario === 'IM_MATCHED' ? '#bbf7d0' : line.scenario === 'IM_SUPERSEDED' ? '#fde68a' : '#fecaca'}`,
                              borderRadius: 6,
                              padding: "10px 12px",
                              marginBottom: 8
                            }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>
                                {line.qty || 0} × {line.item_id || line.raw_reference || 'Unknown'}
                              </div>
                              <div style={{ fontSize: 11, color: textColor, marginTop: 4, fontWeight: 600 }}>
                                {line.scenario === 'IM_MATCHED' && `Matched · $${(line.unit_price || 0).toFixed(2)} ${line.price_source ? line.price_source.toLowerCase() + ' price' : ''}`}
                                {line.scenario === 'IM_SUPERSEDED' && `Superseded from ${line.superseded_from || 'previous item'}${line.substitution_note ? ' — ' + line.substitution_note : ''}`}
                                {line.scenario === 'IM_MULTI_CANDIDATE' && `${line.candidates?.length || 0} candidates in item master — needs review`}
                                {line.scenario === 'IM_LOW_CONFIDENCE' && `${Math.round((line.confidence || 0) * 100)}% confidence`}
                                {line.scenario === 'IM_DISCONTINUED_NO_SUB' && 'Discontinued, no substitute'}
                                {!line.scenario && (line.status === 'NEEDS_REVIEW' ? 'Needs review' : 'Processed')}
                              </div>
                              {line.scenario === 'IM_MULTI_CANDIDATE' && line.candidates && line.candidates.length > 0 && (
                                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
                                  {line.candidates.length} candidates in item master — needs review
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>No line items extracted</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              <Card title="Credit evidence">
                <Row 
                  l="Credit limit" 
                  r={selectedOrder.credit_evidence?.credit_limit ? `$${parseFloat(selectedOrder.credit_evidence.credit_limit).toLocaleString()}` : '$0'} 
                />
                <Row 
                  l="Open AR balance" 
                  r={selectedOrder.credit_evidence?.open_ar ? `$${parseFloat(selectedOrder.credit_evidence.open_ar).toLocaleString()}` : '$0'} 
                />
                <Row 
                  l="Unshipped exposure" 
                  r={selectedOrder.credit_evidence?.unshipped_exposure ? `$${parseFloat(selectedOrder.credit_evidence.unshipped_exposure).toLocaleString()}` : '$0'} 
                />
                <Row 
                  l="Total if released" 
                  r={selectedOrder.credit_evidence?.exposure_total && selectedOrder.credit_evidence?.exposure_pct ? 
                    `$${parseFloat(selectedOrder.credit_evidence.exposure_total).toLocaleString()} · ${selectedOrder.credit_evidence.exposure_pct}% of limit` : 
                    '$0'
                  }
                  rColor={selectedOrder.credit_evidence?.exposure_pct > 150 ? COLORS.red : selectedOrder.credit_evidence?.exposure_pct > 100 ? COLORS.amber : undefined}
                />
                {selectedOrder.credit_evidence?.aging && (
                  <Row 
                    l="Aging 60+ bucket" 
                    r={(() => {
                      const aging = selectedOrder.credit_evidence.aging;
                      const aging60Plus = (aging.d61_90 || 0) + (aging.d90_plus || 0);
                      return aging60Plus > 0 ? `$${aging60Plus.toLocaleString()}` : '$0';
                    })()}
                    rColor={(() => {
                      const aging = selectedOrder.credit_evidence.aging;
                      const aging60Plus = (aging.d61_90 || 0) + (aging.d90_plus || 0);
                      return aging60Plus > 0 ? COLORS.amber : undefined;
                    })()}
                  />
                )}
                <Row 
                  l="Avg days to pay" 
                  r={selectedOrder.credit_evidence?.avg_days_to_pay ? 
                    `${selectedOrder.credit_evidence.avg_days_to_pay} days${selectedOrder.credit_evidence.days_to_pay_trend ? ` (${selectedOrder.credit_evidence.days_to_pay_trend.toLowerCase()})` : ''}` : 
                    'N/A'
                  }
                />
                <Row 
                  l="Contract on file" 
                  r={selectedOrder.credit_evidence?.contract_on_file || 'None'} 
                />
              </Card>

              <Card title="Agent actions performed">
                {selectedOrder.agent_timeline && selectedOrder.agent_timeline.length > 0 ? (
                  selectedOrder.agent_timeline.map((step) => (
                    <Step 
                      key={step.step}
                      n={step.step} 
                      status={step.status} 
                      desc={step.summary || `${step.agent}: ${step.status}`}
                      agent={step.agent}
                      tone={step.status === 'COMPLETED' ? undefined : step.status === 'SKIPPED' ? 's' : 'p'}
                    />
                  ))
                ) : (
                  <div style={{ color: COLORS.muted, fontSize: 13 }}>No agent actions recorded</div>
                )}
              </Card>

              {selectedOrder.credit_evidence?.recommendation && (
                <div style={{ border: "1px solid #f0d9a8", background: COLORS.amberBg, borderRadius: 8, padding: "12px 13px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.amber, letterSpacing: 0.6, marginBottom: 5 }}>
                    AGENT RECOMMENDATION · <SopChip label={`${selectedOrder.sop_id || 'O2C-OPS'} · ${selectedOrder.sop_version || 'v0.1'}`} onClick={() => openSop('credit')} />
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    {selectedOrder.credit_evidence.recommendation}
                    {selectedOrder.credit_evidence.recommended_amount > 0 && (
                      <> Recommended amount: <strong>${parseFloat(selectedOrder.credit_evidence.recommended_amount).toLocaleString()}</strong></>
                    )}
                    {selectedOrder.credit_evidence.clearing_invoice && (
                      <> Clearing invoice: <strong>{selectedOrder.credit_evidence.clearing_invoice}</strong></>
                    )}
                  </div>
                </div>
              )}

              <Card title="Growth signal">
                <Row 
                  l="RFM segment" 
                  r={selectedOrder.rfm?.segment && selectedOrder.rfm?.scores ? 
                    `${selectedOrder.rfm.segment} · ${selectedOrder.rfm.scores}` : 
                    selectedOrder.rfm?.segment || 'N/A'
                  }
                />
                {selectedOrder.growth_signals && selectedOrder.growth_signals.length > 0 ? (
                  selectedOrder.growth_signals.map((signal, idx) => (
                    <Row 
                      key={idx}
                      l={signal.type === 'ATTACHMENT' ? 'Attachment' :
                         signal.type === 'UPGRADE' ? 'Upsell' :
                         signal.type === 'REORDER' ? 'Reorder' :
                         signal.type === 'CONTRACT_LEAKAGE' ? 'Contract leakage' :
                         signal.type}
                      r={signal.description || signal.reason || 'N/A'}
                    />
                  ))
                ) : (
                  <Row l="Signals" r="No growth opportunities identified" />
                )}
                {selectedOrder.growth_signals && selectedOrder.growth_signals.length > 0 && (
                  <Row 
                    l="Est. attach value" 
                    r={(() => {
                      const totalValue = selectedOrder.growth_signals.reduce((sum, s) => sum + (s.est_value || 0), 0);
                      return totalValue > 0 ? `$${totalValue.toLocaleString()}` : 'N/A';
                    })()}
                  />
                )}
              </Card>
            </>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px", display: "flex", gap: 9, alignItems: "center" }}>
          <button 
            onClick={() => handleOrderDecision('REJECTED')} 
            style={{...btnStyle, ...((!selectedOrder || sidebarLoading) && btnDisabledStyle)}}
            disabled={!selectedOrder || sidebarLoading}
          >
            Reject order
          </button>
          <button 
            onClick={() => handleOrderDecision('RELEASED')} 
            style={{...btnStyle, ...((!selectedOrder || sidebarLoading) && btnDisabledStyle)}}
            disabled={!selectedOrder || sidebarLoading}
          >
            Release in full
          </button>
          <button 
            onClick={() => handleOrderDecision('PARTIAL_RELEASE')} 
            style={{ 
              ...btnStyle, 
              background: COLORS.blue, 
              borderColor: COLORS.blue, 
              color: "#fff", 
              marginLeft: "auto",
              ...((!selectedOrder || sidebarLoading) && btnDisabledStyle)
            }}
            disabled={!selectedOrder || sidebarLoading}
          >
            Approve partial release
          </button>
        </div>
      </div>

      {/* SOP panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(760px, 96vw)",
          background: "#fff",
          zIndex: 60,
          transform: sopKey ? "translateX(0)" : "translateX(100%)",
          transition: "transform .22s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {sopKey && (
          <>
            <div style={{ background: "#312e81", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{SOPS[sopKey].title}</h3>
                <div style={{ fontSize: 11.5, color: "#c7d2fe", marginTop: 1 }}>{SOPS[sopKey].sub}</div>
              </div>
              <button onClick={closeSop} style={dcloseStyle}>
                ×
              </button>
            </div>
            <div style={{ overflowY: "auto", padding: "18px 22px", flex: 1 }}>
              <h5 style={{ fontSize: 15, margin: "0 0 4px" }}>Scenario Dispatch</h5>
              <div style={{ fontSize: 11.5, color: COLORS.muted, marginBottom: 14 }}>
                {sopKey === "credit" ? "Used by credit_check_agent · primary branch logic" : "Used by item_master_validation_agent · primary branch logic"}
              </div>
              <SopTable rows={SOPS[sopKey].rows} hasTier={sopKey === "credit"} />

              <div style={{ background: "#fef9e7", border: "1px solid #f0d9a8", borderRadius: 7, padding: "10px 12px", margin: "14px 0", fontSize: 12.5, lineHeight: 1.6 }}>
                <strong>{SOPS[sopKey].matchNote.split(".")[0]}.</strong> {SOPS[sopKey].matchNote.split(".").slice(1).join(".")}
              </div>

              {sopKey === "credit" && (
                <>
                  <h5 style={{ marginTop: 20 }}>Partial release rule</h5>
                  <div style={{ fontSize: 11.5, color: COLORS.muted, marginBottom: 8 }}>Same section</div>
                  <p style={{ fontSize: 12.5, lineHeight: 1.6, color: "#4b5563" }}>{SOPS.credit.partialRule}</p>

                  <h5 style={{ marginTop: 20 }}>Tolerance bands</h5>
                  <ToleranceTable rows={SOPS.credit.tolerance} />
                </>
              )}
            </div>
          </>
        )}

        <div style={{ borderTop: `1px solid ${COLORS.line}`, background: "#fff", padding: "13px 20px", display: "flex", gap: 9, alignItems: "center" }}>
          <button onClick={closeSop} style={btnStyle}>
            Back to decision
          </button>
          <button style={{ ...btnStyle, background: COLORS.blue, borderColor: COLORS.blue, color: "#fff", marginLeft: "auto" }}>Edit SOP</button>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div style={kStyle}>{k}</div>
      <div style={{ fontSize: 13.5, marginTop: 2, fontFamily: mono ? "ui-monospace,monospace" : "inherit" }}>{v}</div>
    </div>
  );
}

function SopTable({ rows, hasTier }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.line}`, fontSize: 12 }}>
      <thead>
        <tr>
          <Th>Scenario</Th>
          <Th>Condition</Th>
          <Th>{hasTier ? "Decision" : "Action"}</Th>
          {hasTier && <Th>Tier</Th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.s} style={r.match ? { background: "#fef9e7", fontWeight: 500 } : {}}>
            <Td first={r.match}>{r.s}</Td>
            <Td>{r.c}</Td>
            <Td>{r.d}</Td>
            {hasTier && <Td>{r.t}</Td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ToleranceTable({ rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${COLORS.line}`, fontSize: 12 }}>
      <thead>
        <tr>
          <Th>Tenure</Th>
          <Th>Payment record</Th>
          <Th>Tolerance</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={r.match ? { background: "#fef9e7", fontWeight: 500 } : {}}>
            <Td first={r.match}>{r.tenure}</Td>
            <Td>{r.record}</Td>
            <Td>{r.tol}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Th({ children }) {
  return (
    <th style={{ background: "#f3f4f6", textAlign: "left", padding: "8px 10px", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#4b5563", borderBottom: `1px solid ${COLORS.line}` }}>
      {children}
    </th>
  );
}

function Td({ children, first }) {
  return (
    <td
      style={{
        padding: "9px 10px",
        borderBottom: "1px solid #f1f3f5",
        verticalAlign: "top",
        borderLeft: first ? "3px solid #d97706" : "none",
      }}
    >
      {children}
    </td>
  );
}

const kStyle = { fontSize: 10.5, color: COLORS.muted, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 600 };

const btnStyle = {
  border: "1px solid #d1d5db",
  background: "#fff",
  borderRadius: 6,
  padding: "7px 13px",
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "inherit",
  color: "#374151",
};

const btnDisabledStyle = {
  opacity: 0.5,
  cursor: "not-allowed"
};

const eyeStyle = {
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  width: 34,
  height: 30,
  cursor: "pointer",
  fontSize: 15,
  color: "#4b5563",
};

const dcloseStyle = {
  background: "none",
  border: 0,
  color: "#c7d2fe",
  fontSize: 28,
  cursor: "pointer",
  lineHeight: 1,
  padding: 0,
  marginLeft: "auto",
  width: 32,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

function tdStyle(i, len) {
  // Match P2P styling: py-5 px-6 = 20px 24px, text-base = 16px
  return { 
    padding: "20px 24px", 
    borderBottom: i === len - 1 ? "none" : `1px solid ${COLORS.line}`, 
    verticalAlign: "top", 
    fontSize: 16 
  };
}
